import { useCallback, useEffect, useState } from 'react';
import Input from 'components/Input';
import { useTranslate } from 'hooks/translation-hooks';
import { useSelector, useDispatch } from 'react-redux';
import { addressOptionsSelector } from 'redux/selectors/caseCreation';
import { getLocationStart } from 'redux/actions/locationActions';
import {
  AddressOption,
  LocationContainer,
  LocateMeIcon,
  LocateMeOption,
  Suggestions,
  Suggestion,
} from './BingAddressStyled';
import Icon from '../Icons/LocateMeIcon';
import {
  currentLanguageSelector,
  selectProvider,
  selectTheme,
} from 'redux/selectors/config';
import { providerToCountry } from 'mappers/providers';

const GoogleAddress = ({ register, errors, setValue, getValues }) => {
  const getGeocodeResultProperty = (result, prop) =>
    result?.results[0].address_components.find(addressComponent =>
      addressComponent.types.includes(prop),
    )?.long_name;
  const t = useTranslate('caseOpening');
  const dispatch = useDispatch();
  const theme = useSelector(selectTheme);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [waitingForGeoLocation, setWaitingForGeoLocation] = useState(false);
  const addressOptions = useSelector(addressOptionsSelector);
  const provider = useSelector(selectProvider);
  const language = useSelector(currentLanguageSelector);
  const country = providerToCountry[provider];
  const acceptSuggestion = useCallback(
    async addressOption => {
      setShowSuggestions(false);
      const options = {
        shouldValidate: true,
        shouldDirty: true,
      };
      const result = await new window.google.maps.Geocoder().geocode({
        address: addressOption.description,
        language,
        componentRestrictions: { country },
      });
      const resultKeys = result?.results[0]?.address_components
        ?.filter(
          ({ types }) =>
            !types.includes('political') &&
            !types.includes('postal_code') &&
            !types.includes('postal_town'),
        )
        .map(({ types }) => types[0]);
      if (resultKeys.length) {
        setValue(
          'address',
          resultKeys
            .map(key => getGeocodeResultProperty(result, key))
            .join(', '),
          options,
        );
      }
      setValue(
        'city',
        getGeocodeResultProperty(result, 'locality') ||
          getGeocodeResultProperty(result, 'postal_town'),
        options,
      );
      setValue(
        'postalCode',
        getGeocodeResultProperty(result, 'postal_code'),
        options,
      );
    },
    [setValue],
  );
  const geoLocateMe = () => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        dispatch(getLocationStart({ coords }));
        setWaitingForGeoLocation(true);
      },
      err => console.log('error', err),
    );
  };
  useEffect(() => {
    if (waitingForGeoLocation && addressOptions?.[0]?.addressLine) {
      setWaitingForGeoLocation(false);
      acceptSuggestion(addressOptions[0]);
    }
  }, [addressOptions, waitingForGeoLocation, setValue, acceptSuggestion]);
  const showMyLocation = !getValues('address');
  return (
    <>
      <Input
        {...register('address', {
          required: true,
          onChange: ev => {
            setShowSuggestions(true);
            dispatch(getLocationStart({ addressLine: ev.target.value }));
          },
        })}
        error={errors.address && 'incident_location-address_line1_error_format'}
        label={t('incident_location-address_line1')}
        placeholder={t('incident_location-address_line1_placeholder')}
        testId="address"
        maxLength={255}
        autoComplete={'off'}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
      >
        {showSuggestions && (
          <Suggestions>
            <Suggestion>
              {showMyLocation ? (
                <LocationContainer>
                  <LocateMeIcon>
                    <Icon color={theme.primaryColor} />
                  </LocateMeIcon>
                  <LocateMeOption onClick={geoLocateMe}>
                    {t('case_address-locate_me')}
                  </LocateMeOption>
                </LocationContainer>
              ) : (
                addressOptions &&
                Array.isArray(addressOptions) &&
                addressOptions.map(addressOption => (
                  <AddressOption
                    onClick={() => acceptSuggestion(addressOption)}
                  >
                    {addressOption.description}
                  </AddressOption>
                ))
              )}
            </Suggestion>
          </Suggestions>
        )}
      </Input>
    </>
  );
};

export default GoogleAddress;
